import { RevIconButton, RevProgressBar } from "@revmatics/catalyst";
import {
  faFileArrowUp,
  faTrash,
  faXmark,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";

type Props = {
  isLoading?: boolean;
  progress?: number;
  className?: string;
  failed?: boolean;
  file: File;
  handleRemove: () => void;
};

export const UploadFileCard = ({
  isLoading,
  progress = 0,
  className = "",
  failed,
  file,
  handleRemove,
}: Props) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const commonStyle =
    "rounded border border-monochrome-200 py-3 px-4 grid grid-cols-[40px_1fr] gap-4 overflow-hidden";

  const formatFileSize = (size: number): string => {
    if (size === 0) return "0 B";
    const k = 1024;
    const units = ["b", "kb", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return `${parseFloat((size / Math.pow(k, i)).toFixed(0))} ${units[i]}`;
  };

  return (
    <div className={`${commonStyle} ${className}`}>
      <div
        className={`w-10 h-10 rounded-full flex justify-center items-center overflow-hidden
        ${failed ? "bg-red-500/12 text-red-600" : "bg-primary-500/12 text-primary-600"}`}
      >
        {url && !isLoading && !failed ? (
          <img src={url} alt="" className="w-full h-full object-cover" />
        ) : (
          <FontAwesomeIcon icon={faFileArrowUp} className="text-xl" />
        )}
      </div>
      <div className="grid grid-cols-[1fr_24px] gap-4">
        <div>
          <p
            className={`typography-body-sm ${failed ? "text-red-600" : "text-monochrome-900"}`}
          >
            {file.name || "Upload failed."}
          </p>
          <div
            className={`flex gap-2 items-center typography-body-xs ${failed ? "text-red-600" : "text-monochrome-600"}`}
          >
            <p>{failed ? "File too large" : formatFileSize(file.size)}</p>
            <span className="text-xl">•</span>
            <p>{isLoading ? "Loading" : failed ? "Failed" : "Complete"}</p>
          </div>

          {isLoading && !failed && (
            <RevProgressBar
              variant="continuous"
              value={progress}
              showLabel
              className="mt-2"
            />
          )}
        </div>
        <RevIconButton
          icon={failed || isLoading ? faXmark : faTrash}
          handleClick={handleRemove}
        />
      </div>
    </div>
  );
};
